import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import { analyticsMiddleware } from './Bft/Middlewares/Analytics.middleware';
import LiveEditing from './Components/LiveEditing';
import CartDrawer from './Bft/Components/CartDrawer';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import ProductViewTracker from './Bft/Components/ProductViewTracker';
import PurchaseTracker from './Bft/Components/PurchaseTracker';
import StoreSearch from './Bft/Components/StoreSearch';
import QliroReport from './Bft/Components/QliroReport';
import SalesData from './Bft/Components/SalesData/SalesData';
import ReturnData from './Bft/Components/SalesData/ReturnData';
import Management from './Bft/Components/Management/Management';
import BftInStockMonitorButton from './Bft/Components/BftInStockMonitorButton';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware, analyticsMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
                <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
        );
    }
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <CartDrawer />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearch />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <Navigation />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearch />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./Components/PersonListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const AddressList = DynamicComponent({
            loader: () => import('./Components/AddressListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <AddressList />
            </Provider>,
            document.getElementById('myPageAddresses')
        );
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./Components/LightboxImages'),
        });
        import('./Reducers/LightboxImages.reducer').then(
            ({ lightboxImages }) => {
                store.injectReducer('lightboxImages', lightboxImages);
                const rootElement = document.getElementById('lightBoxImages');
                const images = Array.from(
                    rootElement.querySelectorAll('template')
                ).map((img) => ({
                    html: img.innerHTML,
                    src: img.dataset.src,
                }));
                renderReact(
                    <Provider store={store}>
                        <LightboxImages images={images} />
                    </Provider>,
                    document.getElementById('lightBoxImages')
                );
            }
        );
    }

    if (document.getElementById('bftProductViewTracker')) {
        const element = document.getElementById('bftProductViewTracker');
        const product = JSON.parse(element.dataset.product || '{}');
        renderReact(
            <ProductViewTracker product={product} />,
            element
        );
    }

    if (document.getElementById('search-result')) {
        const searchTerm = document.getElementById('search-result').dataset.searchTerm;
        if (searchTerm) {
            import('./Bft/Services/Analytics.service').then(({ trackSearch }) => {
                trackSearch(searchTerm);
            });
        }
    }

    if (document.getElementById('bftPurchaseTracker')) {
        const element = document.getElementById('bftPurchaseTracker');
        const order = JSON.parse(element.dataset.order || '{}');
        renderReact(
            <PurchaseTracker order={order} />,
            element
        );
    }

    if (document.getElementById('bftStoreSearch')) {
        const element = document.getElementById('bftStoreSearch');
        const channelSystemId = window.__litium?.requestContext?.channelSystemId;
        renderReact(<StoreSearch channelSystemId={channelSystemId} />, element);
    }

    if (document.getElementById('bftQliroReport')) {
        const element = document.getElementById('bftQliroReport');
        const { brand, batchid, batchlabel, backurl } = element.dataset;
        renderReact(
            <QliroReport
                brand={brand}
                batchId={batchid}
                batchLabel={batchlabel}
                backUrl={backurl}
            />,
            element
        );
    }

    if (document.getElementById('bftSalesData')) {
        renderReact(<SalesData />, document.getElementById('bftSalesData'));
    }

    if (document.getElementById('bftReturnData')) {
        renderReact(<ReturnData />, document.getElementById('bftReturnData'));
    }

    if (document.getElementById('bftManagement')) {
        renderReact(<Management />, document.getElementById('bftManagement'));
    }

    if (document.querySelectorAll('in-stock-monitor').length > 0) {
        Array.from(document.querySelectorAll('in-stock-monitor')).forEach(
            (element) => {
                renderReact(
                    <BftInStockMonitorButton
                        articleNumber={element.dataset.articleNumber}
                        monitored={element.dataset.monitored === 'true'}
                    />,
                    element
                );
            }
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./Components/Slider'),
        });
        Array.from(document.querySelectorAll('.slider')).forEach(
            (slider, index) => {
                const values = [...slider.querySelectorAll('template')].map(
                    (block) => {
                        return {
                            html: block.innerHTML,
                        };
                    }
                );
                if (values.length > 0) {
                    renderReact(<Slider values={values} />, slider);
                }
            }
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    articleNumber,
                    quantityFieldId,
                    href,
                    cssClass,
                    label,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                quantityFieldId,
                                href,
                                cssClass,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.getElementById('orderHistoryPage')) {
        const OrderList = DynamicComponent({
            loader: () => import('./Components/OrderHistoryListContainer'),
        });
        renderReact(
            <Provider store={store}>
                <OrderList />
            </Provider>,
            document.getElementById('orderHistoryPage')
        );
    }
};

bootstrapComponents();
